import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../../components/shareable/modal';
import { useDashboard } from '../../../../context/dashboardContext';

const UpdateConveyorModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isoImagePreview, setIsoImagePreview] = useState(null);
  const [currentIsoImageUrl, setCurrentIsoImageUrl] = useState(null);
  const [isoImageLoading, setIsoImageLoading] = useState(false);
  const [isoImageError, setIsoImageError] = useState(null);
  const [errors, setErrors] = useState({});
  
  const { getLayoutImage, } = useDashboard();

  const fetchLayoutImage = useCallback(async (imageName, { setLoading, setError, setUrl } = {}) => {
    if (!imageName) return;

    const _setLoading = setLoading || setImageLoading;
    const _setError = setError || setImageError;
    const _setUrl = setUrl || setCurrentImageUrl;

    _setLoading(true);
    _setError(null);

    try {
      const result = await getLayoutImage(imageName);

      if (result.success && result.url) {
        _setUrl(result.url);
      } else {
        _setError(result.error || 'Failed to load image');
        console.error('Error loading layout image:', result.error);
      }
    } catch (error) {
      _setError('Error fetching image');
      console.error('Error in fetchLayoutImage:', error);
    } finally {
      _setLoading(false);
    }
  }, [getLayoutImage]);

    useEffect(() => {
    if (initialData) {
      setFormData(prev => {
        const preserveImage = prev.image instanceof File;
        const preserveIsoImage = prev.iso_image instanceof File;
        const newFormData = {
          item_number: initialData.item_number || '',
          wide: initialData.wide || '',
          conveyor_name: initialData.conveyor_name || '',
          conveyor_style: initialData.conveyor_style || '',
          powered_or_non_powered: initialData.powered_or_non_powered || '',
          part_number: initialData.part_number || '',
          length: initialData.length || '',
          babs_cost: initialData.babs_cost || '',
          list: initialData.list || '',
          total: initialData.total || '',
          image_name: initialData.image_name || '',
          image: preserveImage ? prev.image : null,
          iso_image_name: initialData.iso_image_name || '',
          iso_image: preserveIsoImage ? prev.iso_image : null,
        };

        if (!preserveImage) {
          setCurrentImageUrl(null);
          setImageError(null);
          if (initialData.image_name) {
            fetchLayoutImage(initialData.image_name);
          } else if (initialData.presigned_url) {
            setCurrentImageUrl(initialData.presigned_url);
          }
        }

        if (!preserveIsoImage) {
          setCurrentIsoImageUrl(null);
          setIsoImageError(null);
          if (initialData.iso_image_name) {
            fetchLayoutImage(initialData.iso_image_name, {
              setLoading: setIsoImageLoading,
              setError: setIsoImageError,
              setUrl: setCurrentIsoImageUrl,
            });
          }
        }

        return newFormData;
      });
    }
  }, [initialData, fetchLayoutImage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) { setErrors(prev => ({ ...prev, [name]: '' })); }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return false;
    }
    if (file.size > maxSize) {
      alert('Image size must be less than 5MB');
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImageFile(file)) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData((prev) => ({ ...prev, image: file }));
      setCurrentImageUrl(null);
      setImageError(null);
    }
  };

  const handleIsoImageChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImageFile(file)) {
      const previewUrl = URL.createObjectURL(file);
      setIsoImagePreview(previewUrl);
      setFormData((prev) => ({ ...prev, iso_image: file }));
      setCurrentIsoImageUrl(null);
      setIsoImageError(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.part_number || formData.part_number.trim() === '') {
      newErrors.part_number = 'Part number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) { return; }
    
    setUploading(true);
    
    try {
      await onSave(formData);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      if (isoImagePreview) {
        URL.revokeObjectURL(isoImagePreview);
        setIsoImagePreview(null);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, image_name: '' }));
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    setCurrentImageUrl(null);
    setImageError(null);
  };

  const removeIsoImage = () => {
    setFormData((prev) => ({ ...prev, iso_image: null, iso_image_name: '' }));
    if (isoImagePreview) {
      URL.revokeObjectURL(isoImagePreview);
      setIsoImagePreview(null);
    }
    setCurrentIsoImageUrl(null);
    setIsoImageError(null);
  };

  const numberFields = ['item_number', 'length', 'babs_cost', 'list', 'total'];

  // Custom labels for specific fields
  const getFieldLabel = (field) => {
    const labelMap = {
      'conveyor_name': 'Conveyor Name / Description',
      'babs_cost': 'Our Cost'
    };
    
    return labelMap[field] || field.replace(/_/g, ' ');
  };

  // Determine what to show in the image section
  const showImagePreview = imagePreview || currentImageUrl;
  const showUploadArea = !showImagePreview && !imageLoading;

  // Determine what to show in the ISO image section
  const showIsoImagePreview = isoImagePreview || currentIsoImageUrl;
  const showIsoUploadArea = !showIsoImagePreview && !isoImageLoading;

  return (
    <Modal isOpen={isOpen} width='4xl' onClose={onClose}>
      <h2 className="text-lg font-semibold mb-4">Update Conveyor</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-4 max-h-96 overflow-y-auto p-2">
          {/* Image upload field - now full width */}
          <div className="col-span-3">
            <label className="block text-sm font-medium mb-1">Image</label>
            <div className="flex items-center">
              {imageLoading ? (
                <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-gray-500">Loading image...</div>
                </div>
              ) : imageError ? (
                <div className="w-full">
                  {/* Error message */}
                  <div className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg mb-2">
                    <div className="text-red-500 text-sm">
                      No image found for file name:{" "}
                      <span className="font-semibold">{initialData?.image_name || "Unknown"}</span>
                    </div>
                  </div>
                  {/* Upload box */}
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-500">Click to upload new image</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </label>
                </div>
              ) : showImagePreview ? (
                <div className="relative w-full">
                  <div className="flex items-center justify-between w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {/* First column: Image preview */}
                    <div className="flex-shrink-0 w-2/3 h-full flex items-center justify-center">
                      <div className="w-36 h-full flex items-center justify-center overflow-hidden rounded-md bg-gray-100 relative">
                        <img src={imagePreview || currentImageUrl} alt="Preview" className="absolute inset-0 w-full h-full object-contain" onError={() => setImageError('No image found')} />
                      </div>
                    </div>
                    {/* Second column: Image info and remove button */}
                    <div className="flex-1 w-1/3 h-full flex flex-col justify-center items-start pl-4">
                      <div className="text-xs text-gray-600 mb-3">
                        <p className="font-semibold">File Name:</p>
                        <p className="truncate max-w-xs">
                          {formData.image?.name || initialData.image_name || 'No file name'}
                        </p>
                      </div>
                      <button type="button" onClick={removeImage} className="text-red-500 hover:text-red-700 text-sm flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove Image
                      </button>
                    </div>
                  </div>
                </div>
              ) : showUploadArea && (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {initialData?.image_name ? 'Click to change image' : 'Click to upload image'}
                    </p>
                    {initialData?.image_name && (
                      <p className="text-xs text-gray-400 mt-1">Current: {initialData.image_name}</p>
                    )}
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </div>
          </div>
          {/* ISO Image upload field - full width */}
          <div className="col-span-3">
            <label className="block text-sm font-medium mb-1">ISO Image</label>
            <div className="flex items-center">
              {isoImageLoading ? (
                <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-gray-500">Loading ISO image...</div>
                </div>
              ) : isoImageError ? (
                <div className="w-full">
                  <div className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg mb-2">
                    <div className="text-red-500 text-sm">
                      No ISO image found for file name:{" "}
                      <span className="font-semibold">{initialData?.iso_image_name || "Unknown"}</span>
                    </div>
                  </div>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-500">Click to upload new ISO image</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleIsoImageChange} className="hidden" />
                  </label>
                </div>
              ) : showIsoImagePreview ? (
                <div className="relative w-full">
                  <div className="flex items-center justify-between w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="flex-shrink-0 w-2/3 h-full flex items-center justify-center">
                      <div className="w-36 h-full flex items-center justify-center overflow-hidden rounded-md bg-gray-100 relative">
                        <img src={isoImagePreview || currentIsoImageUrl} alt="ISO Preview" className="absolute inset-0 w-full h-full object-contain" onError={() => setIsoImageError('No image found')} />
                      </div>
                    </div>
                    <div className="flex-1 w-1/3 h-full flex flex-col justify-center items-start pl-4">
                      <div className="text-xs text-gray-600 mb-3">
                        <p className="font-semibold">File Name:</p>
                        <p className="truncate max-w-xs">
                          {formData.iso_image?.name || initialData.iso_image_name || 'No file name'}
                        </p>
                      </div>
                      <button type="button" onClick={removeIsoImage} className="text-red-500 hover:text-red-700 text-sm flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove ISO Image
                      </button>
                    </div>
                  </div>
                </div>
              ) : showIsoUploadArea && (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {initialData?.iso_image_name ? 'Click to change ISO image' : 'Click to upload ISO image'}
                    </p>
                    {initialData?.iso_image_name && (
                      <p className="text-xs text-gray-400 mt-1">Current: {initialData.iso_image_name}</p>
                    )}
                  </div>
                  <input type="file" accept="image/*" onChange={handleIsoImageChange} className="hidden" />
                </label>
              )}
            </div>
          </div>
          {/* Form fields */}
          {Object.keys(formData)
            .filter(field => field !== 'image' && field !== 'image_name' && field !== 'iso_image' && field !== 'iso_image_name' && field !== 'temp_quote_qty' && field !== 'infeed_ordered' && field !== 'outfeed_ordered' && field !== 'babs_total')
            .map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium mb-1 capitalize">
                  {getFieldLabel(field)}
                  {field === 'part_number' && <span className="text-red-500 ml-1">*</span>}
                </label>
                {numberFields.includes(field) ? (
                  <input
                    type="number"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleNumberChange}
                    className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${getFieldLabel(field)}`}
                    step={field.includes('cost') || field.includes('price') || field.includes('list') ? '0.01' : '1'}
                  />
                ) : (
                  <input
                    type="text"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${getFieldLabel(field)}`}
                  />
                )}
                {errors[field] && (
                  <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
                )}
              </div>
            ))}
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <button type="button" onClick={onClose} className="px-8 py-2 bg-gray-300 rounded hover:bg-gray-400" disabled={uploading} > Cancel </button>
          <button type="submit" className="px-8 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={uploading} > {uploading ? 'Updating...' : 'Update'} </button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateConveyorModal;

